import { useQuery } from "@apollo/client";
import { useToast } from "@components/common/hooks/toast";
import { Slide } from "@components/common/slider/slide/slide";
import { Slider } from "@components/common/slider/slider";
import { Channel } from "@components/network-connector/channel/channel";
import {
    NETWORK_CONNECTOR_QUERY,
    useDisconnectSocialAccountMutation,
    useInitiateSocialConnectionMutation,
} from "@components/network-connector/network-connector.gql";
import { GQLNetworkConnectorQuery, GQLNetworkConnectorQueryVariables } from "@components/network-connector/network-connector.gql.generated";
import { NetworkConnectorSkeleton } from "@components/network-connector/network-connector.skeleton";
import { resolveBusinessError } from "@utils/business-error-resolver";
import { resolveChannelName } from "@utils/channel-resolver";
import { GQLChannel } from "graphql.generated";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const NetworkConnector: FunctionComponent = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { data, loading, client } = useQuery<GQLNetworkConnectorQuery, GQLNetworkConnectorQueryVariables>(NETWORK_CONNECTOR_QUERY);
    const socialAccounts = data?.me.socialAccounts || [];
    const redirectUri = `${window.location.origin}/network-connector/callback`;
    const [loadingConnect, setLoadingConnect] = useState<GQLChannel | null>(null);
    const { disconnectSocialAccount, loading: loadingConnectSocialAccount } = useDisconnectSocialAccountMutation();
    const { initiateSocialConnection, loading: loadingInitiateSocialConnection } = useInitiateSocialConnectionMutation();

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.data.type === "social-connect-success") {
                setLoadingConnect(null);
                toast.success(
                    t("socialConnector.connect.success", {
                        defaultValue: "{{provider}} has been successfully connected",
                        provider: resolveChannelName(event.data.channel),
                    }),
                    { duration: 3000 },
                );
                client.cache.evict({ fieldName: "me" });
                client.cache.gc();
            }
            if (event.data.type === "social-connect-error") {
                setLoadingConnect(null);
                toast.error(
                    t("socialConnector.connect.error", {
                        defaultValue: "We’re sorry, there was a problem connecting to {{provider}}. Please try again later.",
                        provider: resolveChannelName(event.data.channel),
                    }),
                    { duration: Infinity },
                );
            }
        };

        window.addEventListener("message", handleMessage);

        return () => window.removeEventListener("message", handleMessage);
    }, [toast, t]);

    function onConnectSocialAccount(channel: GQLChannel) {
        setLoadingConnect(channel);
        initiateSocialConnection({
            fetchPolicy: "network-only",
            variables: {
                channel,
                redirectUri,
            },
            onCompleted: (data) => {
                if (data.initiateSocialConnection.error) {
                    toast.error(resolveBusinessError(data.initiateSocialConnection.error.code, t), { duration: Infinity });
                } else {
                    const width = 600;
                    const height = 700;
                    const left = window.screen.width / 2 - width / 2;
                    const top = window.screen.height / 2 - height / 2;
                    window.open(data.initiateSocialConnection.authUrl, "socialConnect", `width=${width},height=${height},left=${left},top=${top}`);
                }
            },
            onError: (error) => {
                setLoadingConnect(null);
                toast.error(error.message, { duration: Infinity });
            },
        });
    }

    function onDisconnectSocialAccount(channel: GQLChannel) {
        setLoadingConnect(channel);
        disconnectSocialAccount({
            fetchPolicy: "network-only",
            variables: {
                channel,
            },
            update: (cache) => {
                cache.evict({ fieldName: "me" });
                cache.gc();
            },
            onCompleted: (data) => {
                setLoadingConnect(null);
                if (data.disconnectSocialAccount.error) {
                    toast.error(resolveBusinessError(data.disconnectSocialAccount.error.code, t), { duration: Infinity });
                } else {
                    toast.success(
                        t("socialConnector.disconnect.success", {
                            defaultValue: "{{provider}} has been successfully disconnected",
                            provider: resolveChannelName(channel),
                        }),
                        { duration: 3000 },
                    );
                }
            },
            onError: (error) => {
                setLoadingConnect(null);
                toast.error(error.message, { duration: Infinity });
            },
        });
    }

    if (loading) return <NetworkConnectorSkeleton />;

    return (
        <div>
            <div className="grid grid-cols-1 gap-y-8">
                <div className="grid grid-cols-1 gap-y-3">
                    <div>
                        <div className="text-lg">
                            {t("socialConnector.music.title", {
                                defaultValue: "Music streaming",
                            })}
                        </div>
                    </div>
                    <Slider slidesPerView={3} spaceBetween={20}>
                        <Slide>
                            <Channel
                                channel={GQLChannel.Spotify}
                                socialAccounts={socialAccounts}
                                onConnect={onConnectSocialAccount}
                                onDisconnect={onDisconnectSocialAccount}
                                isLoading={(loadingConnectSocialAccount || loadingInitiateSocialConnection) && loadingConnect === GQLChannel.Spotify}
                            />
                        </Slide>
                        <Slide>
                            <Channel
                                channel={GQLChannel.Apple}
                                socialAccounts={socialAccounts}
                                onConnect={onConnectSocialAccount}
                                onDisconnect={onDisconnectSocialAccount}
                                isLoading={(loadingConnectSocialAccount || loadingInitiateSocialConnection) && loadingConnect === GQLChannel.Apple}
                            />
                        </Slide>
                    </Slider>
                </div>
                <div className="grid grid-cols-1 gap-y-3">
                    <div>
                        <div className="text-lg">
                            {t("socialConnector.social.title", {
                                defaultValue: "Social media",
                            })}
                        </div>
                    </div>
                    <Slider slidesPerView={3} spaceBetween={20}>
                        <Slide>
                            <Channel
                                channel={GQLChannel.Twitter}
                                socialAccounts={socialAccounts}
                                onConnect={onConnectSocialAccount}
                                onDisconnect={onDisconnectSocialAccount}
                                isLoading={(loadingConnectSocialAccount || loadingInitiateSocialConnection) && loadingConnect === GQLChannel.Twitter}
                            />
                        </Slide>
                        <Slide>
                            <Channel
                                channel={GQLChannel.Facebook}
                                socialAccounts={socialAccounts}
                                onConnect={onConnectSocialAccount}
                                onDisconnect={onDisconnectSocialAccount}
                                isLoading={(loadingConnectSocialAccount || loadingInitiateSocialConnection) && loadingConnect === GQLChannel.Facebook}
                            />
                        </Slide>
                        <Slide>
                            <Channel
                                channel={GQLChannel.Instagram}
                                socialAccounts={socialAccounts}
                                onConnect={onConnectSocialAccount}
                                onDisconnect={onDisconnectSocialAccount}
                                isLoading={
                                    (loadingConnectSocialAccount || loadingInitiateSocialConnection) && loadingConnect === GQLChannel.Instagram
                                }
                            />
                        </Slide>
                        <Slide>
                            <Channel
                                channel={GQLChannel.Pinterest}
                                socialAccounts={socialAccounts}
                                onConnect={onConnectSocialAccount}
                                onDisconnect={onDisconnectSocialAccount}
                                isLoading={
                                    (loadingConnectSocialAccount || loadingInitiateSocialConnection) && loadingConnect === GQLChannel.Pinterest
                                }
                            />
                        </Slide>
                    </Slider>
                </div>
                <div className="grid grid-cols-1 gap-y-3">
                    <div>
                        <div className="text-lg">
                            {t("socialConnector.videos.title", {
                                defaultValue: "Video streaming",
                            })}
                        </div>
                    </div>
                    <Slider slidesPerView={3} spaceBetween={20}>
                        <Slide>
                            <Channel
                                channel={GQLChannel.Youtube}
                                socialAccounts={socialAccounts}
                                onConnect={onConnectSocialAccount}
                                onDisconnect={onDisconnectSocialAccount}
                                isLoading={(loadingConnectSocialAccount || loadingInitiateSocialConnection) && loadingConnect === GQLChannel.Youtube}
                            />
                        </Slide>
                        <Slide>
                            <Channel
                                channel={GQLChannel.Tiktok}
                                socialAccounts={socialAccounts}
                                onConnect={onConnectSocialAccount}
                                onDisconnect={onDisconnectSocialAccount}
                                isLoading={(loadingConnectSocialAccount || loadingInitiateSocialConnection) && loadingConnect === GQLChannel.Tiktok}
                            />
                        </Slide>
                        <Slide>
                            <Channel
                                channel={GQLChannel.Twitch}
                                socialAccounts={socialAccounts}
                                onConnect={onConnectSocialAccount}
                                onDisconnect={onDisconnectSocialAccount}
                                isLoading={(loadingConnectSocialAccount || loadingInitiateSocialConnection) && loadingConnect === GQLChannel.Twitch}
                            />
                        </Slide>
                    </Slider>
                </div>
            </div>
        </div>
    );
};
